import React from "react";

interface HorizontalBarChartProps {
  labels: string[];
  values: number[];
  maxX?: number;
  mirrored?: boolean;
  title?: string;
}

const HorizontalBarChart: React.FC<HorizontalBarChartProps> = ({ labels, values, maxX, mirrored, title }) => {
  // Determine bucket size
  const rawMax = Math.max(...values, 0);
  let bucketSize = 1000;
  if (rawMax < 1000) {
    bucketSize = 100;
  } else if (rawMax > 20000) {
    bucketSize = 5000;
  } else if (rawMax > 10000) {
    bucketSize = 2000;
  }

  // Compute the upper bound (next bucket above max)
  const computedMax = maxX || Math.ceil(rawMax / bucketSize) * bucketSize;

  // Generate bucket markers
  const buckets = [];
  for (let v = 0; v <= computedMax; v += bucketSize) {
    buckets.push(v);
  }

  return (
    <div className="w-full py-8" style={{ marginRight: '3rem' }}>
      {title && <div className="text-lg font-bold mb-6 text-center" style={{ color: 'var(--dark-color-text)' }}>{title}</div>}
      <div className="flex flex-col gap-3">
        {/* Bucket lines and bars overlay, both offset by ml-40 (10rem) */}
        <div className="flex">
          {/* Left label margin - increased to w-56 (14rem) */}
          <div className="w-56" />
          {/* Graph area */}
          <div className="relative flex-1">
            {/* Vertical bucket lines */}
            <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
              <div className="relative w-full h-full">
                {buckets.map((v) => {
                  const leftPercent = (v / computedMax) * 100;
                  return (
                    <div
                      key={v}
                      style={{
                        position: 'absolute',
                        left: `calc(${leftPercent}% - 1px)` ,
                        top: 0,
                        bottom: 0,
                        width: '2px',
                        background: 'var(--dark-color-border-color)',
                        opacity: 0.5,
                      }}
                    />
                  );
                })}
              </div>
            </div>
            {/* Bars and labels */}
            <div className="flex flex-col gap-3 relative z-10">
              {labels.map((label, idx) => {
                const value = values[idx] || 0;
                const percent = Math.min((value / computedMax) * 100, 100);
                return (
                  <div key={label} className="flex items-center w-full">
                    {/* Label is outside graph area, increased to w-56 (14rem) */}
                    <span className="absolute -left-56 w-56 text-xs truncate mr-4 text-right pr-4" style={{ color: 'var(--dark-color-text)', fontSize: '16px' }}>{label}</span>
                    <div className="flex-1 h-5 rounded relative bg-transparent ml-0" style={{ overflow: 'visible' }}>
                      <div
                        className="h-full rounded transition-all duration-300"
                        style={{ width: `${percent}%`, background: '#5E95ED' }}
                      />
                      {/* Value at end of bar */}
                      <span
                        className="absolute"
                        style={{
                          left: percent === 100 ? `calc(${percent}% + 0.5rem)` : `calc(${percent}% + 0.5rem)`,
                          top: 0,
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: '16px',
                          color: 'var(--dark-color-text)',
                          whiteSpace: 'nowrap',
                          zIndex: 20,
                          overflow: 'visible',
                          paddingRight: percent === 100 ? '2.5rem' : undefined,
                        }}
                      >
                        {value >= 1000 ? `${parseFloat((value / 1000).toFixed(1))}k` : parseFloat(value.toFixed(1))}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {/* X axis — perfectly aligned bucket labels */}
      <div className="relative mt-4 flex">
        <div className="w-56" />
        <div className="relative flex-1" style={{ height: '1.5em' }}>
          {buckets.map((v) => {
            const leftPercent = (v / computedMax) * 100;
            // Abbreviate label: show hundreds if < 1000, else k
            let label;
            if (bucketSize < 1000) {
              label = v === 0 ? '0' : `${v / 100}00`;
            } else {
              label = v === 0 ? '0k' : `${v / 1000}k`;
            }
            return (
              <div
                key={v}
                className="text-xs absolute"
                style={{
                  color: 'var(--dark-color-text)',
                  left: `calc(${leftPercent}% - 1em)`,
                  width: '2em',
                  textAlign: 'center',
                }}
              >
                {label}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default HorizontalBarChart;
